import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { CsvExportControl } from '../../components/common/CsvExportControl';
import { DashboardEmailFilter } from '../../components/common/DashboardEmailFilter';
import { DashboardImageControls } from '../../components/common/DashboardImageControls';
import { DashboardImagePreview } from '../../components/common/DashboardImagePreview';
import { MasterLabelLink } from '../../components/common/MasterLabelLink';
import { ReviewStatusControl } from '../../components/common/ReviewStatusControl';
import { useDashboardImageSettings } from '../../hooks/useDashboardImageSettings';
import {
  chainFilterParam,
  enumParam,
  nullableStringParam,
  pageParam,
  stringListParam,
  stringParam,
  useUrlFilterState,
} from '../../hooks/useUrlFilterState';
import { ZoomMeta } from '../../hooks/useZoomPreview';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  BarcodeStatus,
  ForgeDesignDashboardRow,
  ForgeDesignsChainSnapshot,
  ForgeDesignsDashboardResponse,
  ForgeReviewSort,
} from '../../types';
import { CsvValue, downloadCsv, fetchPagedRows } from '../../utils/csvExport';

const PAGE_LIMIT = 50;
const CSV_EXPORT_PAGE_LIMIT = 200;
const GROUP_PREVIEW_LIMIT = 8;

const DESIGNS_CSV_HEADERS: string[] = [
  'design_id',
  'design_ids',
  'chain_id',
  'chain_name',
  'item_number',
  'item_number_design_count',
  'normalized_barcode',
  'barcode_design_count',
  'barcode_status',
  'crop_count',
  'design_count',
  'note_count',
  'is_reviewed',
  'reviewed_count',
  'reviewed_by',
  'reviewed_at',
  'has_front_image',
  'has_back_image',
  'has_barcode_image',
  'preview_viz_url',
  'forge_url',
  'created_at',
  'created_by',
  'created_by_values',
  'updated_at',
  'updated_by',
];

function designsCsvRow(design: ForgeDesignDashboardRow): CsvValue[] {
  return [
    design.designId,
    (design.designIds ?? [design.designId]).join(' '),
    design.chainId,
    design.chainName,
    design.itemNumber,
    design.itemNumberDesignCount,
    design.normalizedBarcode,
    design.barcodeDesignCount,
    (design.barcodeStatusValues ?? [design.barcodeStatus]).join(' '),
    design.cropCount,
    design.designCount ?? 1,
    design.noteCount,
    design.isReviewed,
    design.reviewedCount ?? (design.isReviewed ? 1 : 0),
    design.reviewedBy,
    design.reviewedAt,
    design.hasFrontImage,
    design.hasBackImage,
    design.hasBarcodeImage,
    design.previewVizUrl,
    design.forgeUrl?.startsWith('/')
      ? `${window.location.origin}${design.forgeUrl}`
      : design.forgeUrl,
    design.createdAt,
    design.createdBy,
    (design.createdByValues ?? []).join(' '),
    design.updatedAt,
    design.updatedBy,
  ];
}

const DESIGNS_URL_FILTERS = {
  chain: chainFilterParam(),
  status: enumParam(['unconfirmed', 'pending', 'confirmed'] as const, 'all'),
  q: stringParam(),
  createdBy: stringListParam(),
  updatedBy: stringListParam(),
  view: enumParam(['designs', 'barcodes'] as const, 'designs'),
  sort: enumParam(
    ['newest', 'unreviewed_first', 'reviewed_first', 'barcode_design_count_desc'] as const,
    'newest'
  ),
  page: pageParam(),
  design: nullableStringParam(),
};
const DESIGN_STATUS_OPTIONS: Array<{ value: BarcodeStatus; label: string }> = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'pending', label: 'Pending' },
  { value: 'unconfirmed', label: 'Unconfirmed' },
];
const STATUS_TAB_OPTIONS: Array<{ value: BarcodeStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  ...DESIGN_STATUS_OPTIONS,
];

function formatNumber(value?: number | null): string {
  return (value ?? 0).toLocaleString();
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

function shortId(value: string): string {
  return value.length > 12 ? `${value.slice(0, 6)}...${value.slice(-4)}` : value;
}

function statusLabel(status: string): string {
  if (status === 'confirmed') return 'Confirmed';
  if (status === 'pending') return 'Pending';
  if (status === 'unconfirmed') return 'Unconfirmed';
  return status || 'Unknown';
}

function statusClass(status: string): string {
  if (status === 'confirmed') return 'is-full';
  if (status === 'pending') return 'is-partial';
  return 'is-non-front';
}

function visibleRange(page: number, limit: number, total: number): string {
  if (total === 0) return '0';
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return `${formatNumber(start)}-${formatNumber(end)}`;
}

function isNonEmptyString(value: string | null | undefined): value is string {
  return Boolean(value);
}

type DesignsDashboardUserOptionAliases = ForgeDesignsDashboardResponse & {
  updated_by_options?: string[];
  created_by_options?: string[];
};

type DashboardImageHandlers = Pick<
  ReturnType<typeof useDashboardImageSettings>,
  'handleImageHover' | 'handleImageMove' | 'handleImageLeave'
>;

function designZoomMeta(design: ForgeDesignDashboardRow): ZoomMeta {
  return {
    metadataType: 'design',
    id: design.designId,
    normalizedBarcode: design.normalizedBarcode ?? undefined,
    barcodeStatus: design.barcodeStatus,
    cropCount: design.cropCount,
    noteCount: design.noteCount,
    createdBy: design.createdBy ?? design.createdByValues?.join(', ') ?? undefined,
    createdAt: design.createdAt ?? undefined,
    updatedAt: design.updatedAt ?? undefined,
    updatedBy: design.updatedBy ?? undefined,
  };
}

function createdByOptionsFromDesigns(dashboard: ForgeDesignsDashboardResponse | null): string[] {
  const snakeOptions = (dashboard as DesignsDashboardUserOptionAliases | null)?.created_by_options;
  const rowOptions =
    dashboard?.designs.flatMap((design) => design.createdByValues ?? []).filter(isNonEmptyString) ??
    [];
  return [
    ...new Set([...(dashboard?.createdByOptions ?? []), ...(snakeOptions ?? []), ...rowOptions]),
  ].sort();
}

function updatedByOptionsFromDesigns(dashboard: ForgeDesignsDashboardResponse | null): string[] {
  const snakeOptions = (dashboard as DesignsDashboardUserOptionAliases | null)?.updated_by_options;
  const rowOptions =
    dashboard?.designs.map((design) => design.updatedBy).filter(isNonEmptyString) ?? [];
  return [
    ...new Set([...(dashboard?.updatedByOptions ?? []), ...(snakeOptions ?? []), ...rowOptions]),
  ].sort();
}

function mergeChainSnapshots(
  base: ForgeDesignsChainSnapshot[],
  updates: ForgeDesignsChainSnapshot[]
): ForgeDesignsChainSnapshot[] {
  const updatesById = new Map(updates.map((chain) => [chain.chain.chainId, chain]));
  const baseIds = new Set(base.map((chain) => chain.chain.chainId));
  return [
    ...base.map((chain) => updatesById.get(chain.chain.chainId) ?? chain),
    ...updates.filter((chain) => !baseIds.has(chain.chain.chainId)),
  ].sort((left, right) => left.chain.chainId - right.chain.chainId);
}

function mergeChainRefresh(
  current: ForgeDesignsDashboardResponse,
  refreshed: ForgeDesignsDashboardResponse,
  replaceDesigns: boolean
): ForgeDesignsDashboardResponse {
  const chains = mergeChainSnapshots(current.chains, refreshed.chains);

  return {
    ...current,
    computedAtUtc: refreshed.computedAtUtc,
    supportedChainIds: refreshed.supportedChainIds,
    chains,
    designs: replaceDesigns ? refreshed.designs : current.designs,
    total: replaceDesigns ? refreshed.total : current.total,
    page: replaceDesigns ? refreshed.page : current.page,
    limit: replaceDesigns ? refreshed.limit : current.limit,
    hasNext: replaceDesigns ? refreshed.hasNext : current.hasNext,
  };
}

interface StatusPillProps {
  status: string;
}

const StatusPill: React.FC<StatusPillProps> = ({ status }) => (
  <span className={`dg-scope-pill ${statusClass(status)}`}>{statusLabel(status)}</span>
);

interface ReviewPillProps {
  isReviewed: boolean;
}

const ReviewPill: React.FC<ReviewPillProps> = ({ isReviewed }) => (
  <span className={`dg-scope-pill ${isReviewed ? 'is-reviewed' : 'is-unreviewed'}`}>
    {isReviewed ? 'Reviewed' : 'Needs review'}
  </span>
);

interface ChainCardProps {
  chain: ForgeDesignsChainSnapshot;
  isSelected: boolean;
  isRefreshing: boolean;
  onSelect: () => void;
  onRefresh: () => void;
}

const ChainCard: React.FC<ChainCardProps> = ({
  chain,
  isSelected,
  isRefreshing,
  onSelect,
  onRefresh,
}) => (
  <article className={`dg-chain-card ${isSelected ? 'is-selected' : ''}`}>
    <button type="button" className="dg-chain-select" onClick={onSelect}>
      <span className="dg-chain-title">{chain.chain.chainName}</span>
      <span className="dg-chain-subtitle">chain {chain.chain.chainId}</span>
      <span className="dg-chain-totals">
        <span className="dg-chain-stat">
          <strong>{formatNumber(chain.designCount)}</strong>
          designs
        </span>
        <span className="dg-chain-stat">
          <strong>{formatNumber(chain.creatorCount)}</strong>
          creators
        </span>
      </span>
    </button>
    <div className="dg-chain-bars">
      <span className="dg-chain-bar is-full">{formatNumber(chain.confirmedCount)}</span>
      <span className="dg-chain-bar is-partial">{formatNumber(chain.pendingCount)}</span>
      <span className="dg-chain-bar is-non-front">{formatNumber(chain.unconfirmedCount)}</span>
    </div>
    <button
      type="button"
      className="dg-icon-button"
      aria-label={`Refresh ${chain.chain.chainName}`}
      title={`Refresh ${chain.chain.chainName}`}
      disabled={isRefreshing}
      onClick={onRefresh}
    >
      <RefreshOutlinedIcon className={isRefreshing ? 'is-spinning' : ''} />
    </button>
  </article>
);

interface DesignPreviewProps {
  design: ForgeDesignDashboardRow;
  imageSize: number;
  imageHandlers: DashboardImageHandlers;
}

const DesignPreview: React.FC<DesignPreviewProps> = ({ design, imageSize, imageHandlers }) => {
  const previews = design.previewDesigns?.length
    ? design.previewDesigns.slice(0, GROUP_PREVIEW_LIMIT)
    : [
        {
          designId: design.designId,
          previewVizUrl: design.previewVizUrl,
          isReviewed: design.isReviewed,
        },
      ];
  const hiddenPreviewCount = Math.max(0, (design.designCount ?? previews.length) - previews.length);
  return (
    <div className="dg-thumb-strip" aria-label="Design preview">
      {previews.map((preview) => (
        <DashboardImagePreview
          key={preview.designId}
          imageUrl={preview.previewVizUrl}
          fallback={shortId(preview.designId)}
          size={imageSize}
          className="dg-thumb"
          isReviewed={preview.isReviewed}
          title={preview.createdBy ?? design.normalizedBarcode ?? preview.designId}
          meta={{
            ...designZoomMeta(design),
            id: preview.designId,
            createdBy: preview.createdBy ?? undefined,
          }}
          onImageHover={imageHandlers.handleImageHover}
          onImageMove={imageHandlers.handleImageMove}
          onImageLeave={imageHandlers.handleImageLeave}
        />
      ))}
      {hiddenPreviewCount > 0 ? (
        <div className="dg-thumb dg-thumb-more" style={{ width: imageSize, height: imageSize }}>
          +{formatNumber(hiddenPreviewCount)}
        </div>
      ) : null}
    </div>
  );
};

interface DesignRowProps {
  design: ForgeDesignDashboardRow;
  isSelected: boolean;
  isReviewing: boolean;
  canReview: boolean;
  imageSize: number;
  imageHandlers: DashboardImageHandlers;
  onSelect: () => void;
  onToggleReview: () => void;
}

const DesignRow: React.FC<DesignRowProps> = ({
  design,
  isSelected,
  isReviewing,
  canReview,
  imageSize,
  imageHandlers,
  onSelect,
  onToggleReview,
}) => (
  <tr
    className={`dg-group-row ${isSelected ? 'is-selected' : ''}`}
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.target !== event.currentTarget) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect();
      }
    }}
  >
    <td className="dg-row-reviewed-cell">
      <ReviewStatusControl
        isReviewed={design.isReviewed}
        isBusy={isReviewing}
        canToggle={canReview}
        reviewedLabel="Clear design review"
        unreviewedLabel="Mark design reviewed"
        reviewedBy={design.reviewedBy}
        reviewedAt={design.reviewedAt}
        testId={`design-review-button-${design.designId}`}
        onToggle={(event) => {
          event.stopPropagation();
          onToggleReview();
        }}
      />
      {(design.designCount ?? 1) > 1 ? (
        <span className="dg-review-count">
          {formatNumber(design.reviewedCount)}/{formatNumber(design.designCount)}
        </span>
      ) : null}
    </td>
    <td className="dg-row-examples">
      <DesignPreview design={design} imageSize={imageSize} imageHandlers={imageHandlers} />
    </td>
    <td className="dg-row-id" title={design.designId}>
      {design.forgeUrl ? (
        <MasterLabelLink
          className="dg-id-link"
          to={design.forgeUrl}
          title={`Open design ${design.designId} in Master Label`}
          onClick={(event) => event.stopPropagation()}
        >
          {shortId(design.designId)}
          {(design.designCount ?? design.designIds?.length ?? 1) > 1
            ? ` +${formatNumber((design.designCount ?? design.designIds?.length ?? 1) - 1)}`
            : ''}
        </MasterLabelLink>
      ) : (
        shortId(design.designId)
      )}
    </td>
    <td className="dg-row-count" title={design.normalizedBarcode ?? undefined}>
      {design.normalizedBarcode ?? '-'}
    </td>
    <td className="dg-row-count">{formatNumber(design.barcodeDesignCount)}</td>
    <td className="dg-row-count" title={design.itemNumber ?? undefined}>
      {design.itemNumber ? formatNumber(design.itemNumberDesignCount) : '-'}
    </td>
    <td className="dg-row-type">
      {(design.barcodeStatusValues ?? [design.barcodeStatus]).map((status) => (
        <StatusPill key={status} status={status} />
      ))}
    </td>
    <td className="dg-row-hierarchy">{design.chainName}</td>
    <td className="dg-row-timestamp">{formatTimestamp(design.createdAt)}</td>
    <td className="dg-row-user" title={(design.createdByValues ?? []).join(', ')}>
      {(design.createdByValues ?? []).length
        ? (design.createdByValues ?? []).join(', ')
        : (design.createdBy ?? '-')}
    </td>
    <td className="dg-row-user" title={design.updatedBy ?? undefined}>
      {design.updatedBy ?? '-'}
    </td>
    <td className="dg-row-count">{formatNumber(design.cropCount)}</td>
  </tr>
);

interface DesignPanelProps {
  design: ForgeDesignDashboardRow;
  imageSize: number;
  imageHandlers: DashboardImageHandlers;
}

const DesignPanel: React.FC<DesignPanelProps> = ({ design, imageSize, imageHandlers }) => (
  <div className="dg-inline-panel dg-hierarchy-panel">
    <div className="dg-panel-title">
      <StyleOutlinedIcon />
      <span className="dg-panel-title-text" title={`Design ${design.designId}`}>
        Design {design.designId}
      </span>
    </div>
    <div className="dg-design-preview">
      <DesignPreview
        design={design}
        imageSize={Math.max(imageSize, 54)}
        imageHandlers={imageHandlers}
      />
      <div>
        {(design.barcodeStatusValues ?? [design.barcodeStatus]).map((status) => (
          <StatusPill key={status} status={status} />
        ))}
        <strong>{design.normalizedBarcode ?? shortId(design.designId)}</strong>
        <span>{design.chainName}</span>
      </div>
    </div>
    <div className="dg-selected-summary">
      <span>Created</span>
      <strong>{formatTimestamp(design.createdAt)}</strong>
    </div>
    <div className="dg-selected-summary">
      <span>Created by</span>
      <strong title={(design.createdByValues ?? []).join(', ')}>
        {(design.createdByValues ?? []).join(', ') || design.createdBy || '-'}
      </strong>
    </div>
    <div className="dg-selected-summary">
      <span>Updated by</span>
      <strong title={design.updatedBy ?? undefined}>{design.updatedBy ?? '-'}</strong>
    </div>
    <div className="dg-selected-summary">
      <span>Designs per barcode</span>
      <strong>{formatNumber(design.barcodeDesignCount)}</strong>
    </div>
    <div className="dg-selected-summary">
      <span>Item number</span>
      <strong title={design.itemNumber ?? undefined}>{design.itemNumber ?? '-'}</strong>
    </div>
    <div className="dg-selected-summary">
      <span>Designs per item</span>
      <strong>{design.itemNumber ? formatNumber(design.itemNumberDesignCount) : '-'}</strong>
    </div>
    <div className="dg-selected-summary">
      <span>{design.isReviewed ? 'Reviewed by' : 'Review'}</span>
      <ReviewPill isReviewed={design.isReviewed} />
      <strong title={design.reviewedAt ?? undefined}>
        {design.isReviewed ? design.reviewedBy || 'reviewed' : 'pending'}
      </strong>
    </div>
    <div className="dg-tree">
      <div className="dg-tree-current">
        <span>{formatNumber(design.cropCount)} crops</span>
        <strong>{formatNumber(design.noteCount)} notes</strong>
      </div>
      <div className="dg-tree-current">
        <span>Images</span>
        <strong>
          {[
            design.hasFrontImage ? 'front' : null,
            design.hasBackImage ? 'back' : null,
            design.hasBarcodeImage ? 'barcode' : null,
          ]
            .filter(Boolean)
            .join(', ') || 'none'}
        </strong>
      </div>
    </div>
    {design.forgeUrl ? (
      <MasterLabelLink className="dg-secondary-button dg-panel-link" to={design.forgeUrl}>
        <OpenInNewOutlinedIcon />
        Open in Forge
      </MasterLabelLink>
    ) : null}
  </div>
);

export const DesignsDashboard: React.FC = () => {
  const { token, canDoTasks } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [dashboard, setDashboard] = useState<ForgeDesignsDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filters, updateFilters] = useUrlFilterState(DESIGNS_URL_FILTERS);
  const {
    chain: selectedChainId,
    status: selectedStatus,
    q: committedQuery,
    createdBy: selectedCreatedBy,
    updatedBy: selectedUpdatedBy,
    view: selectedView,
    sort: reviewSort,
    page,
    design: selectedDesignId,
  } = filters;
  const [queryInput, setQueryInput] = useState(committedQuery);
  const [refreshingChainIds, setRefreshingChainIds] = useState<Set<number>>(() => new Set());
  const [allChains, setAllChains] = useState<ForgeDesignsChainSnapshot[] | null>(null);

  useEffect(() => {
    setQueryInput(committedQuery);
  }, [committedQuery]);
  const [reviewingDesignIds, setReviewingDesignIds] = useState<Set<string>>(() => new Set());
  const loadRequestSequence = useRef(0);
  const imageSettings = useDashboardImageSettings('forge-designs-dashboard-crop-size', 42);

  const groupByNormalizedBarcode = selectedView === 'barcodes';
  const viewKey = `${selectedChainId}|${selectedStatus}|${committedQuery}|${selectedCreatedBy.join('\u0000')}|${selectedUpdatedBy.join('\u0000')}|${selectedView}|${reviewSort}|${page}|${selectedDesignId ?? ''}`;
  const viewKeyRef = useRef(viewKey);

  useEffect(() => {
    viewKeyRef.current = viewKey;
  }, [viewKey]);

  const loadDashboard = useCallback(async () => {
    const requestSequence = loadRequestSequence.current + 1;
    loadRequestSequence.current = requestSequence;
    setIsLoading(true);
    try {
      const nextDashboard = await ForgeApiService.fetchDesignsDashboard(token, {
        chainId: selectedChainId === 'all' ? null : selectedChainId,
        barcodeStatus: selectedStatus === 'all' ? null : selectedStatus,
        query: committedQuery || null,
        updatedByValues: selectedUpdatedBy,
        createdByValues: selectedCreatedBy,
        groupByNormalizedBarcode,
        reviewSort,
        page,
        limit: PAGE_LIMIT,
      });
      if (loadRequestSequence.current !== requestSequence) return;
      setDashboard(nextDashboard);
      if (selectedChainId === 'all') {
        setAllChains(nextDashboard.chains);
      }
      setLoadError(null);
    } catch (error) {
      if (loadRequestSequence.current !== requestSequence) return;
      const message = error instanceof Error ? error.message : 'Error loading Designs';
      setDashboard(null);
      setLoadError(message);
      showErrorToast(message);
    } finally {
      if (loadRequestSequence.current === requestSequence) {
        setIsLoading(false);
      }
    }
  }, [
    committedQuery,
    page,
    reviewSort,
    selectedChainId,
    selectedStatus,
    selectedCreatedBy,
    selectedUpdatedBy,
    groupByNormalizedBarcode,
    showErrorToast,
    token,
  ]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  // Chain cards always show every supported chain; when the page opens with a
  // chain filter already in the URL, the filtered response only carries that
  // chain, so fetch the full chain list once to seed the cards.
  const seededAllChainsRef = useRef(false);
  useEffect(() => {
    if (seededAllChainsRef.current) return;
    seededAllChainsRef.current = true;
    if (selectedChainId === 'all') return;
    ForgeApiService.fetchDesignsDashboard(token, {
      chainId: null,
      barcodeStatus: null,
      query: null,
      updatedByValues: [],
      createdByValues: [],
      groupByNormalizedBarcode: false,
      reviewSort: 'newest',
      page: 1,
      limit: 1,
    })
      .then((full) => setAllChains((current) => current ?? full.chains))
      .catch(() => {
        // Supplementary fetch; the cards fall back to the filtered chain only.
      });
  }, [selectedChainId, token]);

  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const handleExportCsv = useCallback(
    async (exportLimit: number) => {
      if (isExportingCsv) return;
      setIsExportingCsv(true);
      try {
        const rows = await fetchPagedRows<ForgeDesignDashboardRow>(
          async (exportPage, pageSize) => {
            const response = await ForgeApiService.fetchDesignsDashboard(token, {
              chainId: selectedChainId === 'all' ? null : selectedChainId,
              barcodeStatus: selectedStatus === 'all' ? null : selectedStatus,
              query: committedQuery || null,
              updatedByValues: selectedUpdatedBy,
              createdByValues: selectedCreatedBy,
              groupByNormalizedBarcode,
              reviewSort,
              page: exportPage,
              limit: pageSize,
            });
            return { rows: response.designs, total: response.total };
          },
          { limit: exportLimit, pageSize: CSV_EXPORT_PAGE_LIMIT }
        );
        const chainSuffix = selectedChainId === 'all' ? '' : `-chain-${selectedChainId}`;
        downloadCsv(
          `forge-designs${chainSuffix}.csv`,
          DESIGNS_CSV_HEADERS,
          rows.map(designsCsvRow)
        );
      } catch {
        showErrorToast('Failed to export designs CSV');
      } finally {
        setIsExportingCsv(false);
      }
    },
    [
      isExportingCsv,
      token,
      selectedChainId,
      selectedStatus,
      committedQuery,
      selectedCreatedBy,
      selectedUpdatedBy,
      groupByNormalizedBarcode,
      reviewSort,
      showErrorToast,
    ]
  );

  const reloadCurrentDashboardSlice = async () => {
    const requestSequence = loadRequestSequence.current + 1;
    loadRequestSequence.current = requestSequence;
    const nextDashboard = await ForgeApiService.fetchDesignsDashboard(token, {
      chainId: selectedChainId === 'all' ? null : selectedChainId,
      barcodeStatus: selectedStatus === 'all' ? null : selectedStatus,
      query: committedQuery || null,
      updatedByValues: selectedUpdatedBy,
      createdByValues: selectedCreatedBy,
      groupByNormalizedBarcode,
      reviewSort,
      page,
      limit: PAGE_LIMIT,
    });
    if (loadRequestSequence.current !== requestSequence) return;
    setDashboard(nextDashboard);
    if (selectedChainId === 'all') {
      setAllChains(nextDashboard.chains);
    }
    setLoadError(null);
  };

  useEffect(() => {
    if (!dashboard || !selectedDesignId) {
      return;
    }
    if (!dashboard.designs.some((design) => design.designId === selectedDesignId)) {
      updateFilters({ design: null });
    }
  }, [dashboard, selectedDesignId, updateFilters]);

  const chainOptions = useMemo(
    () => mergeChainSnapshots(allChains ?? [], dashboard?.chains ?? []),
    [allChains, dashboard?.chains]
  );
  const createdByOptions = useMemo(() => createdByOptionsFromDesigns(dashboard), [dashboard]);
  const updatedByOptions = useMemo(() => updatedByOptionsFromDesigns(dashboard), [dashboard]);

  const refreshRequest = (chainId: number) => ({
    chain_id: chainId,
    barcode_status: selectedStatus === 'all' ? null : selectedStatus,
    query: committedQuery || null,
    updated_by_values: selectedUpdatedBy,
    created_by_values: selectedCreatedBy,
    group_by_normalized_barcode: groupByNormalizedBarcode,
    review_sort: reviewSort,
    page,
    limit: PAGE_LIMIT,
  });

  const handleRefreshChain = async (chainId: number) => {
    const requestViewKey = viewKeyRef.current;
    setRefreshingChainIds((current) => new Set(current).add(chainId));
    try {
      const refreshed = await ForgeApiService.refreshDesignsDashboard(
        token,
        refreshRequest(chainId)
      );
      if (viewKeyRef.current !== requestViewKey) {
        return;
      }
      setDashboard((current) =>
        current ? mergeChainRefresh(current, refreshed, selectedChainId === chainId) : refreshed
      );
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error refreshing Designs');
    } finally {
      setRefreshingChainIds((current) => {
        const next = new Set(current);
        next.delete(chainId);
        return next;
      });
    }
  };

  const handleToggleDesignReview = async (design: ForgeDesignDashboardRow) => {
    if (!canDoTasks) return;
    const nextIsReviewed = !design.isReviewed;
    const designIds = design.designIds?.length ? design.designIds : [design.designId];
    setReviewingDesignIds((current) => new Set([...current, ...designIds]));
    try {
      let updatedCount = 1;
      if ((design.designCount ?? designIds.length) > 1 && design.normalizedBarcode) {
        const response = await ForgeApiService.updateDesignReviewStatusByNormalizedBarcode(token, {
          chain_id: design.chainId,
          normalized_barcode: design.normalizedBarcode,
          is_reviewed: nextIsReviewed,
        });
        updatedCount = response.updatedCount;
      } else {
        await ForgeApiService.updateDesignReviewStatus(token, designIds[0], {
          is_reviewed: nextIsReviewed,
        });
      }
      await reloadCurrentDashboardSlice();
      const subject = updatedCount === 1 ? 'Design' : `${updatedCount} Designs`;
      showSuccessToast(nextIsReviewed ? `${subject} marked reviewed` : `${subject} review cleared`);
    } catch (error) {
      try {
        await reloadCurrentDashboardSlice();
      } catch {
        // Preserve the original write error; a later refresh will reconcile the row.
      }
      showErrorToast(
        error instanceof Error ? error.message : 'Error updating Design review status'
      );
    } finally {
      setReviewingDesignIds((current) => {
        const next = new Set(current);
        designIds.forEach((designId) => next.delete(designId));
        return next;
      });
    }
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    updateFilters({ q: queryInput.trim(), page: 1 });
  };

  const handleSelectChain = (chainId: number | 'all') => {
    updateFilters({ chain: chainId, page: 1 });
  };

  const handleSelectStatus = (status: BarcodeStatus | 'all') => {
    updateFilters({ status, page: 1 });
  };

  const clearSearch = () => {
    setQueryInput('');
    updateFilters({ q: '', createdBy: [], updatedBy: [], sort: 'newest', page: 1 });
  };

  const handleSelectCreatedBy = (nextSelected: string[]) => {
    updateFilters({ createdBy: nextSelected, page: 1 });
  };

  const handleSelectUpdatedBy = (nextSelected: string[]) => {
    updateFilters({ updatedBy: nextSelected, page: 1 });
  };

  const handleReviewSortChange = (nextSort: ForgeReviewSort) => {
    updateFilters({ sort: nextSort, page: 1 });
  };

  const hasListFilters =
    Boolean(committedQuery) ||
    selectedCreatedBy.length > 0 ||
    selectedUpdatedBy.length > 0 ||
    reviewSort !== 'newest';

  return (
    <div className="page-content page-content--wide dg-dashboard-page">
      <section className="forge-card dg-dashboard-hero">
        <div className="dg-hero-bar">
          <div>
            <div className="dg-dashboard-title">Designs</div>
            <div className="rdc-dashboard-copy">
              Design-level barcode snapshots for reviewing image coverage, status, and duplicate
              design patterns.
            </div>
          </div>
          <div className="dg-hero-controls">
            <div className="dg-scope-tabs" role="group" aria-label="Designs status filter">
              {STATUS_TAB_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={selectedStatus === option.value ? 'is-active' : ''}
                  onClick={() => handleSelectStatus(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <DashboardImageControls
              label="Designs"
              cropSize={imageSettings.cropSize}
              onCropSizeChange={imageSettings.setCropSize}
              zoomEnabled={imageSettings.zoomEnabled}
              onToggleZoom={imageSettings.toggleZoom}
            />
          </div>
        </div>
      </section>

      <section className="dg-chain-grid" aria-label="Designs chain stats">
        {chainOptions.map((chain) => (
          <ChainCard
            key={chain.chain.chainId}
            chain={chain}
            isSelected={selectedChainId === chain.chain.chainId}
            isRefreshing={refreshingChainIds.has(chain.chain.chainId)}
            onSelect={() =>
              handleSelectChain(
                selectedChainId === chain.chain.chainId ? 'all' : chain.chain.chainId
              )
            }
            onRefresh={() => void handleRefreshChain(chain.chain.chainId)}
          />
        ))}
      </section>

      <section className="dg-dashboard-grid">
        <div className="forge-card dg-list-panel">
          <div className="dg-list-header">
            <div>
              <div className="dg-panel-title">Designs</div>
              <div className="dg-list-count">
                Showing{' '}
                {visibleRange(
                  dashboard?.page ?? page,
                  dashboard?.limit ?? PAGE_LIMIT,
                  dashboard?.total ?? 0
                )}{' '}
                of {formatNumber(dashboard?.total ?? 0)}{' '}
                {groupByNormalizedBarcode ? 'barcode rows' : 'Designs'}
              </div>
            </div>
            <div className="dg-pagination">
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Previous page"
                disabled={page <= 1 || isLoading}
                onClick={() => updateFilters({ page: Math.max(1, page - 1) })}
              >
                <ChevronLeftOutlinedIcon />
              </button>
              <span>Page {formatNumber(page)}</span>
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Next page"
                disabled={!dashboard?.hasNext || isLoading}
                onClick={() => updateFilters({ page: page + 1 })}
              >
                <ChevronRightOutlinedIcon />
              </button>
            </div>
            <form className="dg-list-controls" onSubmit={handleSearchSubmit}>
              <div className="dg-scope-tabs" role="group" aria-label="Design row grouping">
                <button
                  type="button"
                  className={selectedView === 'designs' ? 'is-active' : ''}
                  onClick={() => updateFilters({ view: 'designs', page: 1, design: null })}
                >
                  Designs
                </button>
                <button
                  type="button"
                  className={selectedView === 'barcodes' ? 'is-active' : ''}
                  onClick={() => updateFilters({ view: 'barcodes', page: 1, design: null })}
                >
                  Barcodes
                </button>
              </div>
              <div className="dg-search dg-search--compact">
                <SearchOutlinedIcon />
                <input
                  type="search"
                  aria-label="Search Designs"
                  placeholder="Design ID or barcode"
                  value={queryInput}
                  onChange={(event) => setQueryInput(event.target.value)}
                />
              </div>
              <DashboardEmailFilter
                ariaLabel="Filter Designs by creator"
                label="Created by"
                noun="creators"
                options={createdByOptions}
                selected={selectedCreatedBy}
                onChange={handleSelectCreatedBy}
              />
              <DashboardEmailFilter
                ariaLabel="Filter Designs by updater"
                label="Updated by"
                noun="updaters"
                options={updatedByOptions}
                selected={selectedUpdatedBy}
                onChange={handleSelectUpdatedBy}
              />
              <label className="dg-sort-control">
                <span>Sort</span>
                <select
                  aria-label="Sort Designs"
                  value={reviewSort}
                  onChange={(event) =>
                    handleReviewSortChange(event.target.value as ForgeReviewSort)
                  }
                >
                  <option value="newest">Newest</option>
                  <option value="barcode_design_count_desc">Most designs per barcode</option>
                  <option value="unreviewed_first">Not reviewed first</option>
                  <option value="reviewed_first">Reviewed first</option>
                </select>
              </label>
              <CsvExportControl
                total={dashboard?.total ?? 0}
                exporting={isExportingCsv}
                buttonClassName="dg-search-clear"
                onExport={(limit) => {
                  void handleExportCsv(limit);
                }}
              />
              {hasListFilters ? (
                <button type="button" className="dg-search-clear" onClick={clearSearch}>
                  Clear
                </button>
              ) : null}
              <button type="submit" className="dg-primary-button">
                Search
              </button>
            </form>
          </div>
          <div className="dg-group-list">
            {isLoading ? (
              <div className="dg-loading-state">Loading Designs...</div>
            ) : loadError ? (
              <div className="dg-empty-state">
                <strong>Designs could not be loaded</strong>
                <span>{loadError}</span>
                <button
                  type="button"
                  className="dg-secondary-button"
                  onClick={() => void loadDashboard()}
                >
                  Retry
                </button>
              </div>
            ) : dashboard?.designs.length ? (
              <div className="dg-group-table-scroll">
                <table className="dg-group-table dg-design-table" aria-label="Designs table">
                  <thead>
                    <tr>
                      <th>Reviewed</th>
                      <th>Preview</th>
                      <th>Design</th>
                      <th>Barcode</th>
                      <th>Designs per barcode</th>
                      <th>Designs per item</th>
                      <th>Status</th>
                      <th>Chain</th>
                      <th>Created</th>
                      <th>Created by</th>
                      <th>Updated by</th>
                      <th>Crops</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.designs.map((design) => (
                      <React.Fragment key={design.designId}>
                        <DesignRow
                          design={design}
                          isSelected={design.designId === selectedDesignId}
                          isReviewing={reviewingDesignIds.has(design.designId)}
                          canReview={canDoTasks}
                          imageSize={imageSettings.cropSize}
                          imageHandlers={imageSettings}
                          onSelect={() =>
                            updateFilters({
                              design: design.designId === selectedDesignId ? null : design.designId,
                            })
                          }
                          onToggleReview={() => void handleToggleDesignReview(design)}
                        />
                        {design.designId === selectedDesignId ? (
                          <tr className="dg-inline-detail-row">
                            <td colSpan={12}>
                              <DesignPanel
                                design={design}
                                imageSize={imageSettings.cropSize}
                                imageHandlers={imageSettings}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="dg-empty-state">No Designs match this view.</div>
            )}
          </div>
        </div>
      </section>
      {imageSettings.ZoomOverlay}
    </div>
  );
};
